import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router";
import { Cta } from "../components/Cta";
import { GoogleButton } from "../features/auth/GoogleButton";
import { Section } from "../components/Section";
import { useAuth } from "../features/auth/context";
import { usePageMeta } from "../lib/usePageMeta";
import { safeNext } from "../lib/safeNext";
import styles from "./Login.module.css";

/**
 * /login — signing IN, and nothing else.
 *
 * ## There is no password field on this page, and there must never be one again
 *
 * This page used to collect an email and a password and hand them to
 * `adapter.login()`. kev-security-model.md forbids exactly that: "Password
 * handling never touches our code — pass 2 uses Hosted UI + authorization code
 * + PKCE, not the browser-side USER_PASSWORD_AUTH the dev prototype used."
 *
 * Credentials are entered on Cognito's Managed Login page. Two things stop the
 * field coming back by accident:
 *   - `AuthPort` has nowhere to put a password (see port.ts).
 *   - The Cognito app client disables userPassword, userSrp, custom and
 *     adminUserPassword flows, so the pool would refuse the call anyway.
 *
 * ## Consent left this page on 2026-08-07, and that is the point
 *
 * This was `/login` and `/signup` in one component behind an `isSignup` flag,
 * and the signup half carried the whole consent notice. It could not carry the
 * things that actually qualify an account — a date of birth, a country of
 * residence — because the next screen was Cognito's form, which renders
 * Cognito's fields.
 *
 * So account creation moved to Signup.tsx, which collects them and calls
 * POST /signup; Cognito's own sign-up form is closed. What is left here is one
 * job: hand a person who already HAS an account to Managed Login.
 *
 * Signing in is not a moment of consent and must not look like one. A returning
 * user gave theirs when they registered, and re-recording it on every sign-in
 * would fill the audit trail with rows that prove nothing about a decision —
 * which is the opposite of what the consent record is for. If the terms change,
 * TERMS_VERSION moves and RequireOnboarded routes them to /welcome to agree
 * again, deliberately.
 */
type Ui = { kind: "idle" } | { kind: "leaving" } | { kind: "error"; message: string };

export function Login() {
  usePageMeta("login");
  const { status, beginSignIn } = useAuth();
  const location = useLocation();
  const [ui, setUi] = useState<Ui>({ kind: "idle" });

  /* Where to land after sign-in: the guard's `from`, else ?next=, else /app.
     Validated — `next` is attacker-controllable via a crafted link — and
     validated AGAIN on the way back, because it round-trips through a URL.
     safeNext also refuses /login, /signup and /welcome as destinations, which
     is what stops this page redirecting to itself. */
  const fromState = (location.state as { from?: string } | null)?.from;
  const fromQuery = new URLSearchParams(location.search).get("next");
  const dest = safeNext(fromState ?? fromQuery);

  if (status === "authenticated") return <Navigate to={dest} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();

    setUi({ kind: "leaving" });
    try {
      /* No consents. This page cannot take them any more and must not appear to:
         signing IN is not the moment consent is given, and a returning user has
         already given theirs. Re-recording it on every sign-in would fill the
         audit trail with rows that prove nothing about a decision. */
      await beginSignIn({ intent: "login", next: dest });
    } catch {
      /* beginSignIn normally never resolves — the page leaves. Reaching here
         means the redirect could not be started at all (missing config), which
         is a deployment fault, not a user error. Say so rather than blame them. */
      setUi({ kind: "error", message: "Sign-in isn't available right now. Try again shortly." });
    }
  }

  return (
    <Section eyebrow="Account" heading="Sign in" tone="ink" level={1}>
      <div className={styles.wrap}>
        <form className={styles.form} onSubmit={onSubmit}>
          <p className={styles.hint}>
            You&rsquo;ll sign in with our identity provider on the next screen. We never see your password.
          </p>

          {ui.kind === "error" && (
            <p className={styles.error} role="alert">
              {ui.message}
            </p>
          )}

          {/* Paired, as equal siblings (Ned, 2026-08-07). Which door you need
              depends on who you are, and this page cannot know — so it stops
              guessing and shows both. Create account goes to OUR form now, not
              to Cognito's: see Signup.tsx. */}
          {/* Renders nothing unless this environment has Google configured. */}
          <GoogleButton next={dest} label="Sign in with Google" />

          <div className={styles.action}>
            <Cta type="submit" disabled={ui.kind === "leaving"}>
              {ui.kind === "leaving" ? "Taking you there…" : "Continue to sign in"}
            </Cta>
            <Cta to="/signup" variant="secondary">
              Create account
            </Cta>
          </div>
        </form>

        {/* Dev affordance. Gated on the mock flag so it cannot reach production
            even by accident — and the production build refuses mocks anyway. */}
        {import.meta.env.VITE_MOCKS === "on" && (
          <p className={styles.devNote}>
            DEV BUILD · MOCK AUTH. &ldquo;Continue&rdquo; signs you in as
            the current Mock Console scenario. No credentials are collected in either build.
          </p>
        )}
      </div>
    </Section>
  );
}
